import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import styles from './LiveDataGrid.module.scss';
import { TableHeader } from '../TableHeader';
import { isEmpty, isEmptyArray, numberWithCommas, formatPercent, formatDollar } from '../../util/functions';
import { getData, downloadData } from '../../api/dashboard';
import DataGrid, { Column, Scrolling, Summary, TotalItem, ColumnFixing, Export } from 'devextreme-react/data-grid';
import { Link } from 'react-router-dom';
import { COLUMN_HEIGHT } from '../../util/dashBoardPageConstants';

const SOCKET_ERROR_MAX_COUNT = 5;
const currentDate = moment();

var noSort = {
  mode: 'none'
};
var noPaging = {
  enabled: false
};

var loadingOptions = {
  enabled: false
};

const formatNumber = {
  type: 'fixedPoint',
  precision: 0
};

const formatNotional = {
  type: 'currency',
  precision: 0
};

const addPercentToData = (data) => {
  if (undefined !== data.value) {
    return formatPercent(data.value);
  }
  return data.value;
};

class LiveDataGrid extends React.Component {
  constructor (props) {
    super(props);
    this.dataGridRef = React.createRef();
    this.state = {
      lastUpdateTime: null,
      data: undefined,
      isDownloadInactive: false,
      failedAttempt: SOCKET_ERROR_MAX_COUNT
    };
    this.populateGridData = this.populateGridData.bind(this);
    this.getLiveGridDataAPI = this.getLiveGridDataAPI.bind(this);
    this.connectToWs = this.connectToWs.bind(this);
    this.reConnectToWs = this.reConnectToWs.bind(this);
    this.download = this.download.bind(this);
    this.renderColumns = this.renderColumns.bind(this);
    this.renderSummary = this.renderSummary.bind(this);
    this.customizeTotal = this.customizeTotal.bind(this);
    this.customizeColumnTotal = this.customizeColumnTotal.bind(this);
    this.customizeSummaryTitle = this.customizeSummaryTitle.bind(this);
  }

  get dataGrid () {
    var gridRef;
    if (this.dataGridRef && this.dataGridRef.current) {
      gridRef = this.dataGridRef.current.instance;
    }
    return gridRef;
  }

  componentDidUpdate (prevProps) {
    if (prevProps.pauseWs !== this.props.pauseWs) {
      const socket = this.state.wsObj;
      // console.log(socket);
      if (this.props.pauseWs && socket) {
        socket.close();
      } else {
        this.connectToWs();
      }
    }
  }

  componentDidMount () {
    // try to get latest data from API
    this.getLiveGridDataAPI();
    // connect to web socket for real time data
    this.connectToWs();
  }

  componentWillUnmount () {
    this.state.wsObj && this.state.wsObj.close();
  }

  getLiveGridDataAPI () {
    const api = this.props.api;
    if (!isEmpty(api)) {
      getData(api)
        .then(data => {
        // set only if socket did not return data
          // console.log('api', data);
          if (!this.state.data) {
            this.populateGridData(data);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }

  download () {
    if (this.state.isDownloadInactive) return;
    // make it inactive for future request until current one is solved
    this.setState({ isDownloadInactive: true });
    // download  data
    let api = this.props.downloadApi;
    if (!isEmpty(api)) {
      const header = this.props.identifier && this.props.identifier.toUpperCase().replaceAll('-', '_');
      const name = `${header}-${currentDate.format('YYYY-MM-DD')}`;
      api = `${api}?date=${currentDate.format('YYYY-MM-DD')}`;
      downloadData(api, name).then(data => {
        this.setState({ isDownloadInactive: false });
      })
        .catch((err) => {
        // error
          this.setState({ isDownloadInactive: false });
          console.log(err);
        });
    } else {
      if (this.dataGrid) {
        this.dataGrid.exportToExcel(false);
      }
      this.setState({ isDownloadInactive: false });
    }
  }

  populateGridData (data) {
    const lastUpdatedField = this.props.lastUpdatedField;
    const totalField = this.props.totalField;
    if (isEmptyArray(data)) {
      // console.log('No Data Found');
      data = [];
    } else {
      // based on contract, response will have only 1 property..
      var responseDataKey = Object.keys(data)[0];
      // console.log(data[responseDataKey]);
      data = data[responseDataKey];
    }
    const lastUpdateTime = data && data[0] && data[0][lastUpdatedField] && moment(data[0][lastUpdatedField], 'YYYY-MM-DD HH:mm:ss.SSS').format('MM/DD/YY hh:mm:ss A');
    const totalCount = (data && data[0] && data[0][totalField] && numberWithCommas(data[0][totalField])) || '';
    this.setState({
      data: data,
      lastUpdateTime: lastUpdateTime,
      totalCount: totalCount,
      failedAttempt: SOCKET_ERROR_MAX_COUNT
    });
  }

  reConnectToWs () {
    if (!this.props.pauseWs) {
      // console.log('open again');
      this.connectToWs();
    }
  }

  connectToWs () {
    const this_ = this;
    const socketApi = this.props.ws;

    if (socketApi && this.state.failedAttempt > 0) {
      const socket = new WebSocket(this.props.ws);
      socket.addEventListener('open', function (event) {
        // console.log('connected', socketApi);
        // socket.send('Hello Server!');
      });
      socket.addEventListener('message', function (event) {
        // console.log('message from server: ' + event.data);
        let data = JSON.parse(event.data);
        this_.populateGridData(data);
      });
      socket.addEventListener('error', function (event) {
        console.log('error in', socketApi);
        console.log('error', event);
        this_.setState({
          failedAttempt: this_.state.failedAttempt - 1
        });
      });
      socket.addEventListener('close', function (event) {
        // console.log('closed', event);
        if (event && event.code !== 1005) {
          this_.reConnectToWs();
        }
      });
      this.setState({
        wsObj: socket
      });
    }
  };

  formatMpid = (data) => {
    const identifier = this.props.identifier;
    if (identifier && identifier.startsWith('ecn')) {
      if (data.data.mpid) {
        const route = identifier.includes('trade') ? 'trade' : 'order';
        return <Link style={{ 'color': 'unset', 'textDecoration': 'unset' }} to={`/${route}?mpid=${data.data.mpid}`}>{data.data.mpid}</Link>;
      }
    }
    return data.data.mpid;
  };

  formatSymbol= (data) => {
    const identifier = this.props.identifier;
    if (identifier && identifier.startsWith('ecn')) {
      if (data.data.symbol) {
        const route = identifier.includes('trade') ? 'trade' : 'order';
        return <Link style={{ 'color': 'unset', 'textDecoration': 'unset' }} to={`/${route}?symbol=${data.data.symbol}`}>{data.data.symbol}</Link>;
      }
    }
    return data.data.symbol;
  };

  renderColumns () {
    const _this = this;
    return this.props.columns && this.props.columns.map(function (item, index) {
      if (item.type === 'number') {
        return <Column height={COLUMN_HEIGHT} format={formatNumber} type='number' key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.type === 'notional') {
        return <Column height={COLUMN_HEIGHT} format={formatNotional} type='number' key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.type === 'percent') {
        return <Column height={COLUMN_HEIGHT} customizeText={addPercentToData} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.dataField === 'mpid') {
        return <Column height={COLUMN_HEIGHT} width='55px' cellRender={_this.formatMpid} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.dataField === 'symbol') {
        return <Column height={COLUMN_HEIGHT} width='55px' cellRender={_this.formatSymbol} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else {
        return <Column height={COLUMN_HEIGHT} width='55px' key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      }
    });
  }

  renderSummary () {
    const _this = this;
    const summaryGroupTitleColumn = this.props.summaryGroupTitleColumn;
    const summaryGroupDataColumn = this.props.summaryGroupDataColumn;
    const summaryGroupDataCustomColumns = this.props.summaryGroupDataCustomColumns;
    const totalField = this.props.totalField;
    if (summaryGroupTitleColumn && summaryGroupDataColumn && totalField) {
      return <Summary >
        <TotalItem
          column={summaryGroupTitleColumn.dataField}
          summaryType='count'
          customizeText={this.customizeSummaryTitle} />
        <TotalItem
          column={summaryGroupDataColumn.dataField}
          summaryType='count'
          cssClass={'dashboardGridSummaryTotalNumber'}
          customizeText={() => _this.customizeTotal} />
      </Summary>;
    } else if (summaryGroupTitleColumn && summaryGroupDataCustomColumns) {
      const _this = this;
      return <Summary >
        <TotalItem
          column={summaryGroupTitleColumn.dataField}
          summaryType='count'
          customizeText={this.customizeSummaryTitle} />

        {summaryGroupDataCustomColumns.map(function (item, index) {
          return <TotalItem
            key={`Summary${index}`}
            column={item.dataField}
            summaryType='count'
            cssClass={'dashboardGridSummaryTotalNumber'}
            customizeText={() => _this.customizeColumnTotal(item.totalField, item.type)} />;
        })}
      </Summary>;
    } else {
      return <div />;
    }
  }

  customizeTotal (data) {
    return `${this.state.totalCount}`;
  }

  customizeColumnTotal (field, type) {
    const data = this.state.data;
    let totalCount;
    if (type === 'notional') {
      totalCount = (data && data[0] && data[0][field] && formatDollar(data[0][field])) || '';
    } else {
      totalCount = (data && data[0] && data[0][field] && numberWithCommas(data[0][field])) || '';
    }
    return totalCount;
  }

  customizeSummaryTitle () {
    return `Total`;
  }

  render () {
    const header = this.props.header;
    const downloadHeader = this.props.identifier && this.props.identifier.toUpperCase().replaceAll('-', '_');
    const downloadFilename = `${downloadHeader}-${currentDate.format('MM/DD/YYYY')}`;
    return (
      <div className={classNames(styles.container)}>
        <TableHeader headerUpperCase={false}
          header={header}
          className={styles.tableHeader}
          download={this.download}
          downloadIconId={this.props.identifier.concat('-download')}
          tableHeaderMargin={false}
          topBorderOnly={false}
          bottomBorderOnly />
        <DataGrid
          ref={this.dataGridRef}
          id={this.props.identifier}
          dataSource={this.state.data}
          columnAutoWidth
          showColumnLines
          showRowLines
          rowAlternationEnabled
          showBorders
          repaintChangesOnly
          paging={noPaging}
          sorting={noSort}
          loadPanel={loadingOptions}
          height={this.props.maxRows ? (this.props.maxRows + 1) * COLUMN_HEIGHT : 450}
          cacheEnabled={false}
        >
          {this.renderColumns()}

          <Scrolling mode='virtual' />
          <Export enabled fileName={downloadFilename} />
          {this.renderSummary()}
          <ColumnFixing enabled />
        </DataGrid>
        <div id='lastUpdated' className={styles.textNote} >Update: <span className={styles.lastUpdated}>{this.state.lastUpdateTime}</span></div>
      </div>

    );
  }
}

LiveDataGrid.propTypes = {
  ws: PropTypes.string.isRequired,
  api: PropTypes.string.isRequired,
  downloadApi: PropTypes.string,
  pauseWs: PropTypes.bool,
  header: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  lastUpdatedField: PropTypes.string.isRequired,
  totalField: PropTypes.string,
  identifier: PropTypes.string.isRequired,
  summaryGroupTitleColumn: PropTypes.object.isRequired,
  summaryGroupDataColumn: PropTypes.object,
  summaryGroupDataCustomColumns: PropTypes.array,
  maxRows: PropTypes.number
};

export default LiveDataGrid;
